import {useCallback, useLayoutEffect, useMemo, useRef} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

export const VERSION_PARAM = 'version';

export interface VersionRef {
	readonly id: string;
	readonly isActive: boolean | null;
	readonly isStandard: boolean | null;
}

const buildSearch = (search: string, id: string | null): string => {
	const params = new URLSearchParams(search);
	if (id === null) {
		params.delete(VERSION_PARAM);
	} else {
		params.set(VERSION_PARAM, id);
	}
	const next = params.toString();
	return next ? `?${next}` : '';
};

const activeIdIn = (versions: ReadonlyArray<VersionRef>) => versions.find(v => v.isActive)?.id ?? null;

/** The URL's version for a version id: none for Standard while it is active, the id itself otherwise. */
const urlIdIn = (versions: ReadonlyArray<VersionRef>, id: string | null): string | null => {
	const version = versions.find(v => v.id === id);
	return version?.isStandard && version.isActive ? null : id;
};

/**
 * Which Baseline version is shown, kept in the URL's `?version=` for the page query to load. A URL with no version
 * shows the active version. Only Standard reads the live Baseline, so every other version, the active one included,
 * needs its id in the URL to load its own rows: a bare URL whose active version is not Standard is replaced with one
 * naming it. The id is left out only for Standard while it is active, and kept for Standard while another version is
 * active, since leaving it out would show the active version instead. An id that is not in the list (a stale link, a
 * deleted version) loads the live rows, so it shows Standard; it is left in the URL, as a version just created is
 * selected before the list that holds it has loaded.
 */
export const useSelectedBaselineVersion = (versions: ReadonlyArray<VersionRef>) => {
	const location = useLocation();
	const history = useHistory();
	const fromUrl = useMemo(() => new URLSearchParams(location.search).get(VERSION_PARAM), [location.search]);
	const activeId = activeIdIn(versions);
	const standardId = versions.find(v => v.isStandard)?.id ?? null;
	const known = versions.some(v => v.id === fromUrl);
	const selectedId = known ? fromUrl : fromUrl !== null ? standardId ?? activeId : activeId;
	const wantedUrlId = known || fromUrl === null ? urlIdIn(versions, selectedId) : fromUrl;

	// The latest render's values, so a selection made from a callback created before the version list changed (the
	// success handler of a delete) still names the right version.
	const latest = useRef({location, versions});
	latest.current = {location, versions};

	const applyUrlId = useCallback(
		(urlId: string | null) => {
			const current = latest.current.location;
			history.replace({
				pathname: current.pathname,
				search: buildSearch(current.search, urlId),
				hash: current.hash,
				state: current.state,
			});
		},
		[history]
	);

	// Replaced in a layout effect, so the active tab shows its own rows from the next query.
	useLayoutEffect(() => {
		if (wantedUrlId !== fromUrl) {
			applyUrlId(wantedUrlId);
		}
	}, [applyUrlId, fromUrl, wantedUrlId]);

	const select = useCallback((id: string) => applyUrlId(urlIdIn(latest.current.versions, id)), [applyUrlId]);
	/**
	 * Shows the active version. `after` is the version list a mutation answered with, which can be newer than the list
	 * this component last rendered.
	 */
	const selectActive = useCallback(
		(after?: ReadonlyArray<VersionRef>) => {
			const list = after ?? latest.current.versions;
			applyUrlId(urlIdIn(list, activeIdIn(list)));
		},
		[applyUrlId]
	);

	return {selectedId, select, selectActive};
};

const COMPARE_OPEN_KEY = 'baselineCompareOpen';
/** Marks the page load that opened the table, so a history entry restored by a browser reload starts closed. */
const PAGE_LOAD = `${Date.now()}-${Math.random()}`;

type HistoryState = Record<string, unknown>;

/**
 * Whether the version comparison is open, kept in the history entry's state rather than in the component. Selecting a
 * version changes the URL and reloads the page, which remounts the section, and the table has to survive that. A fresh
 * visit has no state, and an entry from an earlier page load is ignored, so the table always starts closed. Selecting a
 * version keeps this state, as it forwards the entry's state.
 */
export const useBaselineCompareOpen = (): [boolean, (open: boolean) => void] => {
	const location = useLocation<HistoryState | undefined>();
	const history = useHistory();
	const open = location.state?.[COMPARE_OPEN_KEY] === PAGE_LOAD;
	const setOpen = useCallback(
		(next: boolean) => {
			history.replace({
				pathname: location.pathname,
				search: location.search,
				hash: location.hash,
				state: {...location.state, [COMPARE_OPEN_KEY]: next ? PAGE_LOAD : undefined},
			});
		},
		[history, location.hash, location.pathname, location.search, location.state]
	);
	return [open, setOpen];
};
